import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { api } from "../../../../../Assets/api/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSearchSort } from "../context/SearchSortContext";
import FileExplorerTable from "./FileExplorerTable";
import CircularProgress from "@mui/material/CircularProgress";

const RecycleBinView = ({
  viewType = "list",
  user,
  onBackToFiles,
  onActionComplete = null,
}) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");

  // Get search and sort state from context
  const {
    debouncedSearchText,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    registerFetchFunction,
  } = useSearchSort();

  const [recycleBinItems, setRecycleBinItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [page, setPage] = useState(1); // 1-based for UI
  const [limit, setLimit] = useState(50);
  const requestIdRef = useRef(0);

  // Reset page to 1 when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchText, sortBy, sortOrder]);

  // Validate user before proceeding
  if (!user || !user.slug) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          User information not available
        </Typography>
      </Box>
    );
  }

  // Fetch items when dependencies change
  useEffect(() => {
    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;
    const fetchRecycleBinItems = async () => {
      try {
        setLoading(true);
        // Construct query parameters
        const queryParams = new URLSearchParams();
        if (debouncedSearchText)
          queryParams.append("search", debouncedSearchText);
        queryParams.append("sortBy", sortBy);
        queryParams.append("sortOrder", sortOrder);
        queryParams.append("page", page); // Ensure backend expects 1-based
        queryParams.append("limit", limit);
        if (user?.slug)
          queryParams.append("slug", user.slug.replace("?slug=", ""));

        const response = await api.get(
          `/api/account-storage/recycle-bin/get?${queryParams.toString()}`
        );
        if (isMounted && requestIdRef.current === currentRequestId) {
          if (response?.data?.success) {
            // Handle the new response structure where items are in data.items
            const items = response.data.data?.items || [];
            setRecycleBinItems(Array.isArray(items) ? items : []);
          } else {
            toast.error(
              response?.data?.message || "Failed to fetch recycle bin items"
            );
          }
        }
      } catch (error) {
        if (isMounted && requestIdRef.current === currentRequestId) {
          console.error("Error fetching recycle bin items:", error);
          toast.error(translation.failedToFetchRecycleBinItems);
        }
      } finally {
        if (isMounted && requestIdRef.current === currentRequestId) {
          setLoading(false);
        }
      }
    };
    fetchRecycleBinItems();
    return () => {
      isMounted = false;
    };
  }, [debouncedSearchText, sortBy, sortOrder, page, limit, user]);

  // Register fetch function with context
  useEffect(() => {
    if (user && user.slug) {
      registerFetchFunction(
        "recycleBin",
        ({ searchText, sortBy, sortOrder }) => {
          // This will be handled by the main useEffect
        }
      );
    }
  }, [user, registerFetchFunction]);

  const isItemSelected = useCallback(
    (item) => selectedItems.some((selectedItem) => selectedItem.id === item.id),
    [selectedItems]
  );

  const handleItemSelect = (item) => {
    const isSelected = selectedItems.some(
      (selectedItem) => selectedItem.id === item.id
    );
    if (isSelected) {
      setSelectedItems(
        selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
      );
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  // If there are no items and we're not loading, show empty state
  if (recycleBinItems.length === 0 && !loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          height: "100%",
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {translation.recycleBinIsEmpty}
        </Typography>
        <Button variant="contained" color="primary" onClick={onBackToFiles}>
          {translation.backToFiles}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loader overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(255,255,255,0.6)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      <FileExplorerTable
        items={recycleBinItems}
        isSelected={isItemSelected}
        onItemClick={handleItemSelect}
        viewMode="recycleBin"
        user={user}
        slug={user?.slug}
        onActionComplete={() => requestIdRef.current++}
        page={page}
        setPage={setPage}
        limit={limit}
        onRowsPerPageChange={(e) => {
          const value = parseInt(e.target.value, 10);
          if (!isNaN(value)) {
            setLimit(value);
            setPage(1);
          }
        }}
        onSortChange={(column) => {
          if (sortBy === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
          } else {
            setSortBy(column);
            setSortOrder("asc");
          }
        }}
      />
    </Box>
  );
};

export default RecycleBinView;
